import type { RequestHistory } from '../../api';

export const DEFAULT_HISTORY_PAGE_SIZE = 20;

type HistoryPosition = {
  page: number;
  cursor: { ts: string; id: string } | null;
  asOf: string | null;
};

export type HistoryNavigation = {
  position: HistoryPosition;
  previous: HistoryPosition[];
};

export type HistoryAction = { type: 'next' | 'previous' } | { type: 'jump'; page: number };

export function initialHistoryNavigation(): HistoryNavigation {
  return { position: { page: 1, cursor: null, asOf: null }, previous: [] };
}

export function historyRequestParams(
  filters = new URLSearchParams(),
  limit = DEFAULT_HISTORY_PAGE_SIZE,
  position = initialHistoryNavigation().position,
) {
  const params = new URLSearchParams(filters);
  for (const name of ['page', 'as_of', 'cursor_ts', 'cursor_id']) params.delete(name);
  params.set('limit', String(limit));
  if (position.cursor) {
    params.set('cursor_ts', position.cursor.ts);
    params.set('cursor_id', position.cursor.id);
  }
  // Old cursor-only responses do not supply a snapshot or support page metadata.
  if (!position.cursor || position.asOf) params.set('page', String(position.page));
  if (position.asOf) params.set('as_of', position.asOf);
  return params;
}

export function historyQueryKey(params: URLSearchParams) {
  const canonical = new URLSearchParams(params);
  canonical.sort();
  return ['history', canonical.toString()] as const;
}

export function historyJumpLimit(data: RequestHistory | null) {
  return data?.pagination?.max_jump_page ?? data?.pagination?.total_pages ?? 1;
}

/** All protocol compatibility lives here; saved positions also survive fetch errors. */
export function historyDestination(
  navigation: HistoryNavigation,
  data: RequestHistory | null,
  action: HistoryAction,
): HistoryNavigation | null {
  const pagination = data?.pagination;
  const current = {
    ...navigation.position,
    page: pagination?.page ?? navigation.position.page,
    asOf: pagination?.as_of ?? navigation.position.asOf,
  };

  if (action.type === 'previous') {
    const previous = navigation.previous[navigation.previous.length - 1];
    if (previous) {
      return { position: previous, previous: navigation.previous.slice(0, -1) };
    }
    // A direct jump has no cursor trail. Its preceding page is still within
    // the jump bound, even if the jump failed and there is no response data.
    return current.page > 1 && current.asOf
      ? { position: { ...current, page: current.page - 1, cursor: null }, previous: [] }
      : null;
  }

  if (action.type === 'jump') {
    if (
      !pagination ||
      !Number.isInteger(action.page) ||
      action.page < 1 ||
      action.page > historyJumpLimit(data)
    )
      return null;
    return { position: { page: action.page, cursor: null, asOf: pagination.as_of }, previous: [] };
  }

  const position: HistoryPosition = { page: current.page + 1, cursor: null, asOf: current.asOf };
  if (pagination && pagination.max_jump_page === undefined) {
    // Compatibility with the earlier numbered API, before cursor + page support.
    if (current.page >= pagination.total_pages) return null;
  } else {
    if (!data?.next_cursor_ts || !data.next_cursor_id) return null;
    position.cursor = { ts: data.next_cursor_ts, id: data.next_cursor_id };
  }
  return { position, previous: [...navigation.previous, current] };
}
